'use client'

import { useEffect, useState } from 'react'

interface OverallScoreProps {
  score: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
}

const GRADE_COLORS: Record<string, string> = {
  A: '#22c55e',
  B: '#008dcc',
  C: '#FDC304',
  D: '#f97316',
  F: '#F90B8A',
}

const GRADE_LABELS: Record<string, string> = {
  A: 'Strong Reddit presence with healthy organic mentions across multiple communities.',
  B: 'Good Reddit presence. A few areas could use attention to boost visibility.',
  C: 'Moderate Reddit presence. Several areas need improvement.',
  D: 'Weak Reddit presence. Significant work needed to build community visibility.',
  F: 'Minimal or no Reddit presence detected. Brand monitoring starts here.',
}

export default function OverallScore({ score, grade }: OverallScoreProps) {
  const [displayScore, setDisplayScore] = useState(0)
  const color = GRADE_COLORS[grade]

  useEffect(() => {
    let current = 0
    const step = Math.max(1, Math.floor(score / 40))
    const interval = setInterval(() => {
      current += step
      if (current >= score) {
        current = score
        clearInterval(interval)
      }
      setDisplayScore(current)
    }, 25)
    return () => clearInterval(interval)
  }, [score])

  const circumference = 2 * Math.PI * 54
  const offset = circumference - (displayScore / 100) * circumference

  return (
    <div className='flex flex-col items-center gap-6 py-8'>
      <div className='relative w-40 h-40'>
        <svg className='w-full h-full -rotate-90' viewBox='0 0 120 120'>
          <circle
            cx='60'
            cy='60'
            r='54'
            fill='none'
            stroke='currentColor'
            strokeWidth='8'
            className='text-[var(--muted-foreground)]'
          />
          <circle
            cx='60'
            cy='60'
            r='54'
            fill='none'
            stroke={color}
            strokeWidth='8'
            strokeLinecap='round'
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.8s ease-out' }}
          />
        </svg>
        <div className='absolute inset-0 flex flex-col items-center justify-center'>
          <span className='text-5xl font-bold leading-none' style={{ color }}>
            {grade}
          </span>
          <span className='text-lg font-medium text-muted-foreground'>
            {displayScore}/100
          </span>
        </div>
      </div>
      <p className='text-center text-foreground/80 max-w-md text-sm'>
        {GRADE_LABELS[grade]}
      </p>
    </div>
  )
}
